import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { ACCELERATORS } from "../data/kedve";
import { COUNTRY_CODES, EXPERIENCE_BANDS, COUNTRIES } from "../data/countries";

const DEFAULT_DIAL = COUNTRY_CODES.find((c) => c.iso === "IN")?.dial || "+91";
const DEFAULT_COUNTRY = "India";

export default function Apply() {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    dial: DEFAULT_DIAL,
    mobile: "",
    company: "",
    role: "",
    experience: EXPERIENCE_BANDS[2],
    city: "",
    state: "",
    country: DEFAULT_COUNTRY,
    pincode: "",
    target: "",
    accelerator: ACCELERATORS[0].id,
  });

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const onSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <div data-testid="page-apply" className="text-black">
      <section className="pt-36 pb-16">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="eyebrow opacity-60">Application</div>
            <h1 className="mt-8 font-serif text-6xl md:text-8xl leading-[0.9] tracking-tightest">
              Begin your<br /><span className="italic">application.</span>
            </h1>
          </div>

          <div className="md:col-span-7">
            {submitted ? (
              <div data-testid="apply-success" className="border border-black p-10 md:p-14">
                <div className="eyebrow opacity-60">Application received</div>
                <h2 className="mt-6 font-serif text-4xl md:text-5xl tracking-tightest leading-[0.95]">
                  Thank you, <span className="italic">{form.name || "candidate"}.</span>
                </h2>
                <p className="mt-6 text-base leading-relaxed opacity-80">
                  Someone from kedve will read your application and reach out at <span className="underline">{form.email}</span> within seven working days.
                </p>
                <Link to="/accelerators" className="mt-10 inline-flex items-center gap-2 bg-black text-white rounded-full px-7 py-4 text-sm tracking-wide hover:bg-black/85 transition">
                  Explore the programs <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="border border-black p-8 md:p-10" data-testid="apply-form">
                <FieldGroup title="About you">
                  <Field label="Full name" testId="apply-name" span={2}>
                    <input required value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} />
                  </Field>
                  <Field label="Email" testId="apply-email" span={2}>
                    <input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} />
                  </Field>
                  <Field label="Mobile" testId="apply-mobile" span={2}>
                    <div className="flex gap-3">
                      <select
                        value={form.dial}
                        onChange={(e) => set("dial", e.target.value)}
                        className="bg-transparent border-b border-black py-3 text-lg focus:outline-none pr-2"
                        data-testid="apply-dial"
                        style={{ maxWidth: "150px" }}
                      >
                        {COUNTRY_CODES.map((c) => (
                          <option key={`${c.iso}-${c.dial}`} value={c.dial}>
                            {c.iso} {c.dial}
                          </option>
                        ))}
                      </select>
                      <input
                        required
                        type="tel"
                        inputMode="tel"
                        pattern="[0-9\s\-]{6,15}"
                        value={form.mobile}
                        onChange={(e) => set("mobile", e.target.value)}
                        placeholder="Mobile number"
                        className={`flex-1 ${inputCls} placeholder:opacity-40`}
                      />
                    </div>
                  </Field>
                </FieldGroup>

                <FieldGroup title="Professional">
                  <Field label="Current company" testId="apply-company">
                    <input required value={form.company} onChange={(e) => set("company", e.target.value)} placeholder="e.g. Stripe" className={`${inputCls} placeholder:opacity-40`} />
                  </Field>
                  <Field label="Current role" testId="apply-role">
                    <input required value={form.role} onChange={(e) => set("role", e.target.value)} placeholder="e.g. Senior Product Manager" className={`${inputCls} placeholder:opacity-40`} />
                  </Field>
                  <Field label="Total experience" testId="apply-experience" span={2}>
                    <select value={form.experience} onChange={(e) => set("experience", e.target.value)} className={inputCls}>
                      {EXPERIENCE_BANDS.map((b) => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </Field>
                </FieldGroup>

                <FieldGroup title="Current location">
                  <Field label="City" testId="apply-city">
                    <input required value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="e.g. Mumbai" className={`${inputCls} placeholder:opacity-40`} />
                  </Field>
                  <Field label="State / Region" testId="apply-state">
                    <input required value={form.state} onChange={(e) => set("state", e.target.value)} placeholder="e.g. Maharashtra" className={`${inputCls} placeholder:opacity-40`} />
                  </Field>
                  <Field label="Country" testId="apply-country">
                    <select value={form.country} onChange={(e) => set("country", e.target.value)} className={inputCls}>
                      {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </Field>
                  <Field label="Pincode / ZIP" testId="apply-pincode">
                    <input required value={form.pincode} onChange={(e) => set("pincode", e.target.value)} placeholder="e.g. 400001" className={`${inputCls} placeholder:opacity-40`} />
                  </Field>
                </FieldGroup>

                <FieldGroup title="Your intent" last>
                  <Field label="Where you are headed" testId="apply-target" span={2}>
                    <input value={form.target} onChange={(e) => set("target", e.target.value)} placeholder="e.g. Director of Product" className={`${inputCls} placeholder:opacity-40`} />
                  </Field>
                  <Field label="Program of interest" testId="apply-accelerator" span={2}>
                    <select value={form.accelerator} onChange={(e) => set("accelerator", e.target.value)} className={inputCls}>
                      {ACCELERATORS.map((a) => (
                        <option key={a.id} value={a.id}>{a.title}</option>
                      ))}
                    </select>
                  </Field>
                </FieldGroup>

                <button type="submit" data-testid="apply-submit" className="mt-4 inline-flex items-center gap-2 bg-black text-white rounded-full px-8 py-4 text-sm tracking-wide hover:bg-black/85 transition w-full justify-center">
                  Submit application <ArrowUpRight className="w-4 h-4" />
                </button>

                <p className="mt-6 text-xs opacity-60">By submitting, you consent to kedve reviewing your application.</p>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

const inputCls = "w-full bg-transparent border-b border-black py-3 text-lg focus:outline-none";

function FieldGroup({ title, children, last }) {
  return (
    <div className={`${last ? "" : "mb-10 pb-2"}`}>
      <div className="eyebrow opacity-40 mb-4">— {title}</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8">
        {children}
      </div>
    </div>
  );
}

function Field({ label, testId, span = 1, children }) {
  return (
    <label className={`block ${span === 2 ? "md:col-span-2" : ""}`} data-testid={testId}>
      <span className="eyebrow opacity-60">{label}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
